import { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft, Trophy, Plus, Users, TrendingUp, Medal,
  ChevronDown, ChevronUp, Edit2, X, Shield, Crown,
  Settings, Trash2, HelpCircle, RefreshCw, Copy, Download, AlertTriangle, Edit, Search, MessageSquare, CheckCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ThemedBackground } from '../components/ThemedBackground';
import { Faction, VictoryType, PlayerRole, ROLE_NAMES, ROLE_FACTION, PenaltyRecord, PENALTY_NAMES, PenaltyType } from '../types/stats';
import { DojosRow, SeasonsRow } from '../types/database';
import { saveToStorage, loadFromStorage, removeFromStorage, STORAGE_KEYS } from '../utils/storage';
import { statsDataService } from '../modules/stats/dataService';
import { gameService, GameData } from '../modules/stats/gameService';
import { dojoSeasonService } from '../modules/stats/dojoSeasonService';
import { resetService, ResetState } from '../modules/stats/resetService';
import { StatsCalculator, getGoodCount } from '../modules/stats/calculator';
import {
  PlayerInfo,
  LeaderboardPlayer,
  GameHistoryItem,
  ScoreCalculation,
  DebugPlayerRecord,
  LeaderboardType,
} from '../modules/stats/types';

import RulesModal from '../components/RulesModal';
import DojoSeasonModal from '../components/DojoSeasonModal';
import EditConfirmModal from '../components/EditConfirmModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import ResetDataModal from '../components/ResetDataModal';
import { supabase } from '../lib/supabase';
import TimeFilter from '../components/TimeFilter';
import DebugModal from '../components/DebugModal';
import GameRecordModal from '../components/GameRecordModal';
import DataAuditModal from '../components/DataAuditModal';
import ExportImportModal from '../components/ExportImportModal';




export default function GameStats() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'overview' | 'leaderboard' | 'history'>('overview');
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [allPlayerOptions, setAllPlayerOptions] = useState<{ value: string; label: string }[]>([]);
  const [isOverviewExpanded, setIsOverviewExpanded] = useState(false);
  const [isLeaderboardExpanded, setIsLeaderboardExpanded] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showDojoSeasonModal, setShowDojoSeasonModal] = useState(false);
  const [playerStats, setPlayerStats] = useState<LeaderboardPlayer[]>([]);
  const [totalGames, setTotalGames] = useState(0);
  const [factionWins, setFactionWins] = useState({ good: 0, evil: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [canResetStats, setCanResetStats] = useState(false);

  const [playerCount, setPlayerCount] = useState<number>(5);
  const [winningFaction, setWinningFaction] = useState<Faction>('good');
  const [victoryType, setVictoryType] = useState<VictoryType>('mission');
  const [mvpPlayer, setMvpPlayer] = useState<number | ''>('');
  const [taskResults, setTaskResults] = useState<(boolean | null)[]>([null, null, null, null, null]);
  const [playerInfos, setPlayerInfos] = useState<PlayerInfo[]>([]);
  const [calculatedScores, setCalculatedScores] = useState<ScoreCalculation[] | null>(null);
  const [editingGameId, setEditingGameId] = useState<number | null>(null);
  const [gameDate, setGameDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [gameNumber, setGameNumber] = useState<number>(1);
  const [gameDojoId, setGameDojoId] = useState<string>('');
  const [gameSeasonId, setGameSeasonId] = useState<string>('');

  const [historyGames, setHistoryGames] = useState<GameHistoryItem[]>([]);
  const [penaltyRecords, setPenaltyRecords] = useState<PenaltyRecord[]>([]);
  const [historyFilter, setHistoryFilter] = useState<'all' | 'good' | 'evil' | 'violation'>('all');
  const [expandedGameId, setExpandedGameId] = useState<number | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(100);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  
  const [leaderboardType, setLeaderboardType] = useState<LeaderboardType>('score');
  const [factionSortMode, setFactionSortMode] = useState<'good' | 'evil'>('good');

  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [pendingEditGame, setPendingEditGame] = useState<GameHistoryItem | null>(null);

  // V282: 分步加载状态
  const [loadingStates, setLoadingStates] = useState({
    overview: true,
    history: false,
    penalty: false,
  });
  const [showDeleteGameConfirm, setShowDeleteGameConfirm] = useState(false);
  const [editingPenaltyRecord, setEditingPenaltyRecord] = useState<PenaltyRecord | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const [debugMode, setDebugMode] = useState(false);
  const [debugPlayerDetails, setDebugPlayerDetails] = useState<DebugPlayerRecord[]>([]);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugTargetPlayer, setDebugTargetPlayer] = useState<LeaderboardPlayer | null>(null);

  const [dojoFilter, setDojoFilter] = useState<string>('all');
  const [seasonFilter, setSeasonFilter] = useState<string>('all');
  const [dojos, setDojos] = useState<DojosRow[]>([]);
  const [seasons, setSeasons] = useState<SeasonsRow[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [timeRange, setTimeRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });

  const [resetState, setResetState] = useState<ResetState>({
    selectedDojos: [],
    selectedSeasons: [],
    passwordInput: '',
    passwordAttempts: 0,
    lockedUntil: null,
  });
  const [showDataResetModal, setShowDataResetModal] = useState(false);
  const [resetPreviewCount, setResetPreviewCount] = useState(0);
  const [showDataAuditModal, setShowDataAuditModal] = useState(false);
  const [showExportImportModal, setShowExportImportModal] = useState(false);

  // V270: 评论功能相关状态
  const [commentGameId, setCommentGameId] = useState<number | null>(null);
  const [commentText, setCommentText] = useState('');
  const [commentNickname, setCommentNickname] = useState('');
  const [gameComments, setGameComments] = useState<Array<{
    id: number;
    game_record_id: number;
    player_nickname: string;
    comment_text: string;
    created_at: string;
  }>>([]);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);

  // V546: 懒加载玩家数据相关状态
  const [loadingPlayerGames, setLoadingPlayerGames] = useState<Set<number>>(new Set());
  const [loadedPlayerGames, setLoadedPlayerGames] = useState<Set<number>>(new Set());

  const DISPLAY_COUNT_DEFAULT = 10;
  const DISPLAY_COUNT_EXPANDED = 100;

  useEffect(() => {
    const savedState = loadFromStorage<{
      playerCount: number;
      winningFaction: Faction;
      victoryType: VictoryType;
      mvpPlayer: number | '';
      selfDestruct: number | '';
      wrongKill: number | '';
      bugPlayer: number | '';
      taskResults: (boolean | null)[];
      playerInfos: PlayerInfo[];
    }>(STORAGE_KEYS.GAME_RECORD);

    if (savedState) {
      setPlayerCount(savedState.playerCount);
      setWinningFaction(savedState.winningFaction);
      setVictoryType(savedState.victoryType);
      setMvpPlayer(savedState.mvpPlayer);
      setTaskResults(savedState.taskResults);
      setPlayerInfos(savedState.playerInfos);
    }
  }, []);

  useEffect(() => {
    const stateToSave = {
      playerCount,
      winningFaction,
      victoryType,
      mvpPlayer,
      taskResults,
      playerInfos,
    };
    saveToStorage(STORAGE_KEYS.GAME_RECORD, stateToSave);
  }, [playerCount, winningFaction, victoryType, mvpPlayer, taskResults, playerInfos]);

  useEffect(() => {
    if (window.location.search.includes('debug=1')) {
      setDebugMode(true);
      console.log('[V100] 调试模式已启用');
    }

    const titleElement = document.getElementById('stats-title');
    if (titleElement) {
      let pressTimer: ReturnType<typeof setTimeout>;
      const startPress = () => {
        pressTimer = setTimeout(() => {
          setDebugMode(prev => !prev);
          console.log('[V100] 调试模式切换');
        }, 2000);
      };
      const endPress = () => clearTimeout(pressTimer);

      titleElement.addEventListener('touchstart', startPress);
      titleElement.addEventListener('touchend', endPress);
      titleElement.addEventListener('mousedown', startPress);
      titleElement.addEventListener('mouseup', endPress);
    }
  }, []);

  useEffect(() => {
    loadDojosAndSeasons();

    const userName = localStorage.getItem('user_name') || '访客';
    const emplId = localStorage.getItem('emplId') || '';
    const hasResetPermission = userName === '王翱翔' || emplId === '王翱翔';
    setCanResetStats(hasResetPermission);
    
    // V524: 首屏只加载核心数据（总览）
    loadOverviewData();
  }, []);

  useEffect(() => {
    // V524: 只在筛选条件变化时刷新总览数据
    if (activeTab === 'overview') {
      loadOverviewData();
    }
  }, [dojoFilter, seasonFilter, timeRange]);

  useEffect(() => {
    // V524: 延迟加载 - 切换到对应 Tab 时才加载数据
    if (activeTab === 'history') {
      loadHistoryData(true); // 传递 true 重置分页
      loadPenaltyData();
    }
    if (activeTab === 'leaderboard') {
      loadPenaltyData();
    }
  }, [activeTab, dojoFilter, seasonFilter, timeRange]);

  const CURRENT_VERSION = 'V157';

  const initStats = async () => {
    try {
      const savedVersion = localStorage.getItem('avalon_last_calculated_version');
      console.log('[V111] 版本校验:', { savedVersion, currentVersion: CURRENT_VERSION });
      
      // V607: 版本不匹配时清空版本号缓存（触发器方案不再需要全量重算）
      if (savedVersion !== CURRENT_VERSION) {
        console.log('[V607] 版本更新，统计数据将由触发器自动维护');
        localStorage.setItem('avalon_last_calculated_version', CURRENT_VERSION);
      }
      
      // V524/V546: 首屏不等待历史数据加载，改为后台异步加载
      // 分阶段加载：先加载核心统计数据，再延迟加载历史记录
      Promise.all([
        loadPlayerStats(),
      ]).catch(err => console.error('[V524] 核心数据加载失败:', err));
      
      // V546: 延迟 300ms 后开始加载历史记录（避免阻塞首屏渲染）
      setTimeout(() => {
        Promise.all([
          loadHistoryGames(),
          loadPenaltyRecords(),
        ]).catch(err => console.error('[V546] 后台历史数据加载失败:', err));
      }, 300);
      
      // V111: 后台异步执行数据一致性守护检查
      setTimeout(async () => {
        try {
          const { isConsistent, differences } = await statsDataService.checkDataConsistency(dojoFilter, seasonFilter);
          if (!isConsistent) {
            console.log(`[V111] 后台检测到 ${differences} 处数据差异，建议运行全量重算`);
          }
        } catch (e) {
          console.error('[V111] 后台一致性检查失败:', e);
        }
      }, 2000);
    } catch (err) {
      console.error('[V111] 初始化统计失败:', err);
    }
  };

  const loadDojosAndSeasons = async () => {
    try {
      const [dojosData, seasonsData] = await Promise.all([
        dojoSeasonService.loadDojos(),
        dojoSeasonService.loadSeasons(),
      ]);
      setDojos(dojosData);
      setSeasons(seasonsData);
    } catch (err) {
      console.error('[V100] 加载道场/赛季失败:', err);
    }
  };

  const loadOverviewData = async () => {
    setLoadingStates(prev => ({ ...prev, overview: true }));
    try {
      let data;
      if (timeRange.startDate && timeRange.endDate) {
        data = await statsDataService.loadPlayerStatsWithDateRange(
          dojoFilter === 'all' ? undefined : Number(dojoFilter),
          seasonFilter === 'all' ? undefined : Number(seasonFilter),
          timeRange.startDate,
          timeRange.endDate
        );
      } else {
        data = await statsDataService.loadPlayerStats(dojoFilter, seasonFilter);
      }
      setPlayerStats(data.playerStats);
      setTotalGames(data.totalGames);
      setFactionWins(data.factionWins);
    } catch (err) {
      console.error('[V524] 加载玩家统计失败:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, overview: false }));
    }
  };

  const loadHistoryData = async (reset: boolean = false) => {
    if (loadingStates.history && !reset) return;
    
    setLoadingStates(prev => ({ ...prev, history: true }));
    try {
      const page = reset ? 1 : historyPage;
      const { data: games, total, hasMore } = await statsDataService.loadHistoryGames(
        dojoFilter, 
        seasonFilter,
        page,
        historyPageSize
      );
      
      let filteredGames = games;
      if (timeRange.startDate && timeRange.endDate) {
        const startStr = timeRange.startDate.toISOString().split('T')[0];
        const endStr = timeRange.endDate.toISOString().split('T')[0];
        filteredGames = games.filter(g => {
          const gameDate = g.game_date || g.created_at?.split('T')[0] || '';
          return gameDate >= startStr && gameDate <= endStr;
        });
      }
      
      if (reset) {
        setHistoryGames(filteredGames);
      } else {
        setHistoryGames(prev => [...prev, ...filteredGames]);
      }
      
      setHistoryTotal(total);
      setHistoryHasMore(hasMore);
      setHistoryPage(page);
    } catch (err) {
      console.error('[V610] 加载历史记录失败:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, history: false }));
    }
  };

  const handleLoadMoreHistory = async () => {
    if (isLoadingMore || !historyHasMore) return;
    
    setIsLoadingMore(true);
    try {
      const nextPage = historyPage + 1;
      const { data: games, total, hasMore } = await statsDataService.loadHistoryGames(
        dojoFilter,
        seasonFilter,
        nextPage,
        historyPageSize
      );
      
      let filteredGames = games;
      if (timeRange.startDate && timeRange.endDate) {
        const startStr = timeRange.startDate.toISOString().split('T')[0];
        const endStr = timeRange.endDate.toISOString().split('T')[0];
        filteredGames = games.filter(g => {
          const gameDate = g.game_date || g.created_at?.split('T')[0] || '';
          return gameDate >= startStr && gameDate <= endStr;
        });
      }
      
      setHistoryGames(prev => [...prev, ...filteredGames]);
      setHistoryTotal(total);
      setHistoryHasMore(hasMore);
      setHistoryPage(nextPage);
    } catch (err) {
      console.error('[V610] 加载更多历史记录失败:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const loadPenaltyData = async () => {
    // V524: 避免重复加载
    if (loadingStates.penalty) return;
    
    setLoadingStates(prev => ({ ...prev, penalty: true }));
    try {
      const penalties = await statsDataService.getAllPenaltyRecords(
        dojoFilter !== 'all' ? Number(dojoFilter) : undefined,
        seasonFilter !== 'all' ? Number(seasonFilter) : undefined
      );
      
      let filteredRecords = penalties;
      if (timeRange.startDate && timeRange.endDate) {
        const startStr = timeRange.startDate.toISOString().split('T')[0];
        const endStr = timeRange.endDate.toISOString().split('T')[0];
        filteredRecords = penalties.filter(r => {
          const recordDate = r.penalty_date || r.created_at?.split('T')[0] || '';
          return recordDate >= startStr && recordDate <= endStr;
        });
      }
      
      setPenaltyRecords(filteredRecords);
    } catch (err) {
      console.error('[V524] 加载扣分记录失败:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, penalty: false }));
    }
  };
  const loadPlayerStats = async () => {
    try {
      let data;
      if (timeRange.startDate && timeRange.endDate) {
        data = await statsDataService.loadPlayerStatsWithDateRange(
          dojoFilter === 'all' ? undefined : Number(dojoFilter),
          seasonFilter === 'all' ? undefined : Number(seasonFilter),
          timeRange.startDate,
          timeRange.endDate
        );
      } else {
        data = await statsDataService.loadPlayerStats(dojoFilter, seasonFilter);
      }
      setPlayerStats(data.playerStats);
      setTotalGames(data.totalGames);
      setFactionWins(data.factionWins);
    } catch (err) {
      console.error('[V524] 加载玩家统计失败:', err);
    }
  };

  const loadHistoryGames = async () => {
    try {
      setIsLoadingMore(true);
      
      // V550: 先执行诊断查询，了解数据库中的实际数据范围
      const diagnoseResult = await statsDataService.diagnoseGameDateRange();
      console.log('[V550-DIAGNOSE] 数据库诊断结果:', diagnoseResult);
      
      // V544-P0: 时间范围筛选下推到数据库层
      const timeRangeParam = timeRange.startDate && timeRange.endDate 
        ? { start: timeRange.startDate, end: timeRange.endDate }
        : undefined;
      
      console.log('[V550] 当前时间范围筛选条件:', timeRangeParam);
      
      // V546: 只加载基本信息，玩家数据懒加载
      const games = await statsDataService.loadHistoryGamesBasic(dojoFilter, seasonFilter, timeRangeParam);
      
      setHistoryGames(games);
      setIsLoadingMore(false);
    } catch (err) {
      console.error('[V526] 加载历史记录失败:', err);
      setIsLoadingMore(false);
    }
  };

  // V546: 懒加载单个游戏的玩家数据
  const loadGamePlayersIfNeeded = async (gameId: number) => {
    // 如果已经加载过，直接返回
    if (loadedPlayerGames.has(gameId)) {
      return;
    }

    // 如果正在加载中，等待完成
    if (loadingPlayerGames.has(gameId)) {
      return;
    }

    try {
      setLoadingPlayerGames(prev => new Set(prev).add(gameId));
      
      const players = await statsDataService.loadGamePlayers(gameId);
      
      // 更新游戏记录的 players 字段
      setHistoryGames(prevGames => 
        prevGames.map(game => {
          if (game.id === gameId) {
            const mvpPlayers = players.filter(p => p.is_mvp);
            return {
              ...game,
              players,
              mvp_names: mvpPlayers.map(p => p.player_nickname || `${p.player_number}号`),
            };
          }
          return game;
        })
      );
      
      setLoadedPlayerGames(prev => new Set(prev).add(gameId));
    } catch (err) {
      console.error(`[V546] 懒加载游戏 ${gameId} 的玩家数据失败:`, err);
    } finally {
      setLoadingPlayerGames(prev => {
        const next = new Set(prev);
        next.delete(gameId);
        return next;
      });
    }
  };



  const loadPenaltyRecords = async () => {
    try {
      const records = await statsDataService.getAllPenaltyRecords(
        dojoFilter === 'all' ? undefined : Number(dojoFilter),
        seasonFilter === 'all' ? undefined : Number(seasonFilter)
      );
      
      // V259: 应用时间筛选
      let filteredRecords = records;
      if (timeRange.startDate && timeRange.endDate) {
        const startStr = timeRange.startDate.toISOString().split('T')[0];
        const endStr = timeRange.endDate.toISOString().split('T')[0];
        filteredRecords = records.filter(r => {
          const recordDate = r.penalty_date || r.created_at?.split('T')[0] || '';
          return recordDate >= startStr && recordDate <= endStr;
        });
      }
      
      setPenaltyRecords(filteredRecords);
    } catch (err) {
      console.error('[V524] 加载扣分记录失败:', err);
    }
  };

  // V259: 时间筛选变化
  const handleTimeFilter = (startDate: Date, endDate: Date) => {
    console.log('[TimeFilter] 时间范围:', startDate, 'to', endDate);
    setTimeRange({ startDate, endDate });
  };

  // V259: 赛季变化
  const handleSeasonChange = (seasonId: string) => {
    console.log('[TimeFilter] 赛季变化:', seasonId);
    setSeasonFilter(seasonId);
  };

  // V259: 道场变化
  const handleDojoChange = (dojoId: string) => {
    console.log('[TimeFilter] 道场变化:', dojoId);
    setDojoFilter(dojoId);
  };

  // V270: 评论功能相关函数
  const loadGameComments = async (gameRecordId: number) => {
    try {
      const { data, error } = await supabase
        .from('game_comments')
        .select('*')
        .eq('game_record_id', gameRecordId)
        .eq('is_deleted', 'n')
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      setGameComments(data || []);
    } catch (error) {
      console.error('[V270] 加载评论失败:', error);
    }
  };

  const handleSubmitComment = async () => {
    if (!commentGameId || !commentText.trim()) return;
    
    setIsSubmittingComment(true);
    try {
      const nickname = commentNickname.trim() || localStorage.getItem('user_name') || '匿名玩家';
      
      if (editingCommentId) {
        // 编辑现有评论
        const { error } = await supabase.from('game_comments').update({
          comment_text: commentText.trim(),
          player_nickname: nickname,
        }).eq('id', editingCommentId);
        
        if (error) throw error;
        setEditingCommentId(null);
      } else {
        // 新增评论
        const { error } = await supabase.from('game_comments').insert({
          game_record_id: commentGameId,
          player_nickname: nickname,
          comment_text: commentText.trim(),
        });
        
        if (error) throw error;
      }
      
      setCommentText('');
      setCommentNickname('');
      await loadGameComments(commentGameId);
    } catch (error) {
      console.error('[V270] 提交评论失败:', error);
      alert('提交评论失败，请重试');
    } finally {
      setIsSubmittingComment(false);
    }
  };
  
  const handleEditComment = (comment: { id: number; player_nickname: string; comment_text: string }) => {
    setEditingCommentId(comment.id);
    setCommentNickname(comment.player_nickname);
    setCommentText(comment.comment_text);
  };
  
  const handleDeleteComment = async (commentId: number) => {
    if (!confirm('确定要删除这条评论吗？')) return;
    
    try {
      const { error } = await supabase.from('game_comments').delete().eq('id', commentId);
      
      if (error) throw error;
      
      if (commentGameId) {
        await loadGameComments(commentGameId);
      }
    } catch (error) {
      console.error('[V270] 删除评论失败:', error);
      alert('删除评论失败，请重试');
    }
  };
  
  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setCommentNickname('');
    setCommentText('');
  };

  const handleOpenCommentModal = async (gameId: number) => {
    setCommentGameId(gameId);
    await loadGameComments(gameId);
  };

  const handleCloseCommentModal = () => {
    setCommentGameId(null);
    setCommentText('');
    setGameComments([]);
  };

  const handleEditPenaltyRecord = (record: PenaltyRecord) => {
    setEditingPenaltyRecord(record);
  };

  const handleDeletePenaltyRecord = async (id: number) => {
    setPendingDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePenaltyRecord = async () => {
    if (!pendingDeleteId) return;
    setIsDeleting(true);
    try {
      await statsDataService.deletePenaltyRecord(pendingDeleteId);
      await loadPenaltyRecords();
      setShowDeleteConfirm(false);
      setPendingDeleteId(null);
    } catch (error) {
      console.error('删除处罚记录失败:', error);
      alert('删除失败，请重试');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveEditedPenalty = async () => {
    if (!editingPenaltyRecord || !editingPenaltyRecord.id) return;
    setIsSubmitting(true);
    try {
      await statsDataService.updatePenaltyRecord(editingPenaltyRecord.id, editingPenaltyRecord);
      await loadPenaltyRecords();
      setEditingPenaltyRecord(null);
    } catch (error) {
      console.error('更新处罚记录失败:', error);
      alert('更新失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredPlayerStats = useMemo(() => {
    if (!selectedPlayers || selectedPlayers.length === 0 || !selectedPlayers[0]) return playerStats;
    const searchText = selectedPlayers[0].toLowerCase();
    return playerStats.filter(player =>
      player.player_name?.toLowerCase().includes(searchText)
    );
  }, [playerStats, selectedPlayers]);

  // V122: 玩家积分榜数据 - 始终按积分降序排序
  const playerScoreLeaderboardData = useMemo(() => {
    return [...filteredPlayerStats]
      .sort((a, b) => {
        const scoreDiff = (b.total_score || 0) - (a.total_score || 0);
        if (scoreDiff !== 0) return scoreDiff;
        const winRateDiff = (b.win_rate || 0) - (a.win_rate || 0);
        if (winRateDiff !== 0) return winRateDiff;
        return (b.total_games || 0) - (a.total_games || 0);
      });
  }, [filteredPlayerStats]);

  // V122: 根据排行榜类型对数据进行排序
  const sortedLeaderboardData = useMemo(() => {
    const data = [...filteredPlayerStats];
    
    switch (leaderboardType) {
      case 'score':
        // V122: 个人胜率榜 - 按胜率降序，场次降序
        return data
          .filter(p => p.total_games >= 10) // 至少10场才能上榜
          .sort((a, b) => {
            const winRateDiff = (b.win_rate || 0) - (a.win_rate || 0);
            if (winRateDiff !== 0) return winRateDiff;
            return (b.total_games || 0) - (a.total_games || 0);
          });
      
      case 'faction':
        // V122: 阵营胜率排行
        if (factionSortMode === 'good') {
          // 正义阵营胜率 = 正义胜场 / 正义总场次
          return data
            .filter(p => ((p.good_wins || 0) + (p.good_losses || 0)) >= 10)
            .map(p => ({
              ...p,
              factionWinRate: ((p.good_wins || 0) + (p.good_losses || 0)) > 0 
                ? Math.round(((p.good_wins || 0) / ((p.good_wins || 0) + (p.good_losses || 0))) * 100) 
                : 0
            }))
            .sort((a, b) => {
              const aRate = (a as any).factionWinRate;
              const bRate = (b as any).factionWinRate;
              return bRate - aRate;
            });
        } else {
          // 邪恶阵营胜率 = 邪恶胜场 / 邪恶总场次
          return data
            .filter(p => ((p.evil_wins || 0) + (p.evil_losses || 0)) >= 10)
            .map(p => ({
              ...p,
              factionWinRate: ((p.evil_wins || 0) + (p.evil_losses || 0)) > 0 
                ? Math.round(((p.evil_wins || 0) / ((p.evil_wins || 0) + (p.evil_losses || 0))) * 100) 
                : 0
            }))
            .sort((a, b) => {
              const aRate = (a as any).factionWinRate;
              const bRate = (b as any).factionWinRate;
              return bRate - aRate;
            });
        }
      
      case 'mvp':
        // V122: MVP荣誉排行 - 按MVP次数降序，积分降序
        return data.sort((a, b) => {
          const mvpDiff = (b.mvp_count || 0) - (a.mvp_count || 0);
          if (mvpDiff !== 0) return mvpDiff;
          return (b.total_score || 0) - (a.total_score || 0);
        });
      
      case 'percival':
        // V122: 派西维尔排行 - 按派西维尔胜率降序
        return data
          .filter(p => (p.percival_games || 0) > 0)
          .map(p => ({
            ...p,
            roleWinRate: p.percival_games > 0 ? Math.round(((p.percival_wins || 0) / p.percival_games) * 100) : 0
          }))
          .sort((a, b) => {
            const aRate = (a as any).roleWinRate;
            const bRate = (b as any).roleWinRate;
            const rateDiff = bRate - aRate;
            if (rateDiff !== 0) return rateDiff;
            return (b.percival_games || 0) - (a.percival_games || 0);
          });
      
      case 'merlin':
        // V122: 梅林排行 - 按梅林胜率降序
        return data
          .filter(p => (p.merlin_games || 0) > 0)
          .map(p => ({
            ...p,
            roleWinRate: p.merlin_games > 0 ? Math.round(((p.merlin_wins || 0) / p.merlin_games) * 100) : 0
          }))
          .sort((a, b) => {
            const aRate = (a as any).roleWinRate;
            const bRate = (b as any).roleWinRate;
            const rateDiff = bRate - aRate;
            if (rateDiff !== 0) return rateDiff;
            return (b.merlin_games || 0) - (a.merlin_games || 0);
          });
      
      case 'assassin':
        // V122: 刺客排行 - 按刺客成功率降序
        return data
          .filter(p => (p.assassin_games || 0) > 0)
          .map(p => ({
            ...p,
            roleWinRate: p.assassin_games > 0 ? Math.round(((p.assassin_wins || 0) / p.assassin_games) * 100) : 0
          }))
          .sort((a, b) => {
            const aRate = (a as any).roleWinRate;
            const bRate = (b as any).roleWinRate;
            const rateDiff = bRate - aRate;
            if (rateDiff !== 0) return rateDiff;
            return (b.assassin_games || 0) - (a.assassin_games || 0);
          });
      
      default:
        return data;
    }
  }, [filteredPlayerStats, leaderboardType, factionSortMode]);

  const calculateScores = async () => {
    try {
      const calculator = new StatsCalculator(playerCount);
      const scores = await calculator.calculateGameScoresAsync(
        playerInfos,
        winningFaction,
        victoryType,
        mvpPlayer
      );
      setCalculatedScores(scores);
    } catch (err) {
      console.error('[V100] 计算积分失败:', err);
    }
  };

  useEffect(() => {
    const hasPlayers = playerInfos.some(p => p.player_role);
    if (hasPlayers && winningFaction && victoryType) {
      calculateScores();
    }
  }, [winningFaction, victoryType, mvpPlayer, playerInfos]);

  useEffect(() => {
    const goodCount = getGoodCount(playerCount);
    const newInfos: PlayerInfo[] = [];

    for (let i = 1; i <= playerCount; i++) {
      const existingPlayer = playerInfos.find(p => p.player_number === i);
      newInfos.push({
        player_number: i,
        player_nickname: existingPlayer?.player_nickname || '',
        player_role: existingPlayer?.player_role || '',
        faction: i <= goodCount ? 'good' : 'evil',
      });
    }

    setPlayerInfos(newInfos);
    setMvpPlayer('');
  }, [playerCount]);

  const handlePlayerInfoChange = (playerNumber: number, field: keyof PlayerInfo, value: string) => {
    setPlayerInfos(prev => prev.map(info => {
      if (info.player_number !== playerNumber) return info;
      const updated = { ...info, [field]: value };
      if (field === 'player_role' && value) {
        updated.faction = ROLE_FACTION[value as PlayerRole];
      }
      return updated;
    }));
  };

  // V113: 显示Toast提示
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const toast = document.createElement('div');
    toast.className = `fixed top-4 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 transition-all duration-300 ${
      type === 'success' ? 'bg-green-500/90 text-white' : 'bg-red-500/90 text-white'
    }`;
    toast.innerHTML = `
      ${type === 'success' 
        ? '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>' 
        : '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>'
      }
      <span class="font-medium">${message}</span>
    `;
    document.body.appendChild(toast);
    
    // 动画进入
    requestAnimationFrame(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translate(-50%, -20px)';
      requestAnimationFrame(() => {
        toast.style.opacity = '1';
        toast.style.transform = 'translate(-50%, 0)';
      });
    });
    
    // 3秒后移除
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translate(-50%, -20px)';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  };

  // V115: 带重试的请求封装 - 优化错误处理逻辑
  const executeWithRetry = async <T,>(
    fn: () => Promise<T>,
    operationName: string,
    maxRetries: number = 1
  ): Promise<T> => {
    let lastError: any;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await fn();
        return result;
      } catch (err: any) {
        lastError = err;
        console.log(`[V115] ${operationName} 第${attempt + 1}次尝试失败:`, err);
        
        // V115: 检查是否是schema cache错误，如果是则忽略（操作可能已成功）
        const errorMessage = err?.message || err?.error?.message || '';
        const isSchemaCacheError = errorMessage.includes('schema cache') || 
                                   errorMessage.includes('Could not find the table') ||
                                   errorMessage.includes('player_stats');
        
        if (isSchemaCacheError) {
          console.log(`[V115] ${operationName} 检测到schema cache错误，操作可能已成功，跳过重试`);
          // 返回一个空结果表示成功
          return undefined as T;
        }
        
        // 如果不是最后一次尝试，等待后重试
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    
    throw lastError;
  };

  const handleSubmit = async () => {
    if (!calculatedScores) return;

    setIsSubmitting(true);
    
    try {
      const gameData: GameData = {
        playerCount,
        winningFaction,
        victoryType,
        taskResults,
        goodMvp: winningFaction === 'good' ? (mvpPlayer || null) : null,
        evilMvp: winningFaction === 'evil' ? (mvpPlayer || null) : null,
        selfDestruct: null,
        wrongKill: null,
        bugPlayer: null,
        gameDate,
        gameNumber,
        dojoId: gameDojoId,
        seasonId: gameSeasonId,
      };

      console.log('[V115] 开始保存游戏记录:', { editingGameId, gameData });
      
      // V115: 使用重试机制
      await executeWithRetry(
        () => gameService.saveGameRecord(gameData, playerInfos, calculatedScores, editingGameId),
        '保存游戏记录',
        1
      );

      console.log('[V115] 保存成功，刷新数据...');
      
      // V115: 强制刷新数据，确保列表数据是最新的
      await loadPlayerStats();
      await loadHistoryGames();
      
      // V210: 清除草稿
      clearDraft();
      
      // V115: 重置表单并关闭弹窗
      resetForm();
      
      // V115: 显示成功提示
      showToast(editingGameId ? '保存成功' : '游戏记录保存成功', 'success');
    } catch (err: any) {
      console.error('[V115] 保存失败:', err);
      
      // V115: 明确判断真正的错误
      const errorCode = err?.code || err?.error?.code;
      const errorStatus = err?.status || err?.statusCode || err?.error?.status;
      const errorMessage = err?.message || err?.error?.message;
      
      // V115: 检查是否是schema cache错误或player_stats相关错误，如果是则视为成功
      const isSchemaCacheError = errorMessage?.includes('schema cache') || 
                                 errorMessage?.includes('Could not find the table') ||
                                 errorMessage?.includes('player_stats');
      
      if (isSchemaCacheError) {
        console.log('[V115] 检测到schema cache错误，操作可能已成功，刷新数据...');
        await loadPlayerStats();
        await loadHistoryGames();
        resetForm();
        showToast(editingGameId ? '保存成功' : '游戏记录保存成功', 'success');
        return;
      }
      
      // V115: 只有明确收到错误响应时才显示错误
      const isRealError = errorCode || errorStatus || 
        (errorMessage && !errorMessage.includes('undefined') && !errorMessage.includes('null'));
      
      if (!isRealError) {
        console.log('[V115] 无明确错误信息，数据可能已成功保存，刷新数据...');
        await loadPlayerStats();
        await loadHistoryGames();
        clearDraft();
        resetForm();
        showToast(editingGameId ? '保存成功' : '游戏记录保存成功', 'success');
        return;
      }
      
      let errorMsg = '保存失败';
      
      if (errorCode === '42501' || errorStatus === 403) {
        errorMsg = '保存失败：您没有权限执行此操作';
      } else if (errorCode === '23505') {
        errorMsg = '保存失败：数据冲突（重复记录）';
      } else if (errorCode === '23503') {
        errorMsg = '保存失败：关联数据不存在';
      } else if (errorMessage?.includes('network') || errorMessage?.includes('timeout') || errorMessage?.includes('fetch')) {
        errorMsg = '保存失败：网络超时，请检查网络后重试';
      } else if (errorMessage) {
        errorMsg = `保存失败：${errorMessage}`;
      }
      
      // V115: 显示错误Toast而非alert
      showToast(errorMsg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteGame = async () => {
    if (!editingGameId) return;

    setIsDeleting(true);
    
    try {
      console.log('[V115] 开始删除游戏记录:', editingGameId);
      
      // V115: 先关闭弹窗，避免重复触发
      setShowDeleteGameConfirm(false);
      
      // V115: 使用重试机制
      await executeWithRetry(
        () => gameService.deleteGameRecord(editingGameId),
        '删除游戏记录',
        1
      );
      
      console.log('[V115] 删除成功，刷新数据...');
      
      // V115: 强制刷新数据
      await loadHistoryGames();
      await loadPlayerStats();
      resetForm();
      
      // V115: 显示成功提示
      showToast('删除成功', 'success');
    } catch (err: any) {
      console.error('[V115] 删除失败:', err);
      
      // V115: 明确判断真正的错误
      const errorCode = err?.code || err?.error?.code;
      const errorStatus = err?.status || err?.statusCode || err?.error?.status;
      const errorMessage = err?.message || err?.error?.message;
      
      // V115: 检查是否是schema cache错误或player_stats相关错误，如果是则视为成功
      const isSchemaCacheError = errorMessage?.includes('schema cache') || 
                                 errorMessage?.includes('Could not find the table') ||
                                 errorMessage?.includes('player_stats');
      
      if (isSchemaCacheError) {
        console.log('[V115] 检测到schema cache错误，操作可能已成功，刷新数据...');
        await loadHistoryGames();
        await loadPlayerStats();
        resetForm();
        showToast('删除成功', 'success');
        return;
      }
      
      // V115: 只有明确收到错误响应时才显示错误
      const isRealError = errorCode || errorStatus || 
        (errorMessage && !errorMessage.includes('undefined') && !errorMessage.includes('null'));
      
      if (!isRealError) {
        console.log('[V115] 无明确错误信息，数据可能已成功删除，刷新数据...');
        await loadHistoryGames();
        await loadPlayerStats();
        resetForm();
        showToast('删除成功', 'success');
        return;
      }
      
      let errorMsg = '删除失败';
      
      if (errorCode === '42501' || errorStatus === 403) {
        errorMsg = '删除失败：您没有权限执行此操作';
      } else if (errorMessage?.includes('network') || errorMessage?.includes('timeout') || errorMessage?.includes('fetch')) {
        errorMsg = '删除失败：网络超时';
      } else if (errorMessage) {
        errorMsg = `删除失败：${errorMessage}`;
      }
      
      // V115: 显示错误Toast而非alert
      showToast(errorMsg, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const resetForm = () => {
    setShowAddModal(false);
    setEditingGameId(null);
    setCalculatedScores(null);
    setMvpPlayer('');
    setTaskResults([null, null, null, null, null]);
    setGameDojoId('');
    setGameSeasonId('');
    removeFromStorage(STORAGE_KEYS.GAME_RECORD);
    removeFromStorage(STORAGE_KEYS.GAME_RECORD_DRAFT);
  };

  const saveDraft = () => {
    const draftData = {
      playerCount,
      winningFaction,
      victoryType,
      taskResults,
      mvpPlayer,
      playerInfos,
      gameDate,
      gameNumber,
      gameDojoId,
      gameSeasonId,
    };
    saveToStorage(STORAGE_KEYS.GAME_RECORD_DRAFT, draftData, 24 * 7);
    showToast('草稿已保存', 'success');
  };

  const loadDraft = () => {
    const draftData = loadFromStorage<{
      playerCount: number;
      winningFaction: Faction;
      victoryType: VictoryType;
      taskResults: (boolean | null)[];
      mvpPlayer: number | '';
      playerInfos: PlayerInfo[];
      gameDate: string;
      gameNumber: number;
      gameDojoId: string;
      gameSeasonId: string;
    }>(STORAGE_KEYS.GAME_RECORD_DRAFT, 24 * 7);

    if (draftData) {
      setPlayerCount(draftData.playerCount);
      setWinningFaction(draftData.winningFaction);
      setVictoryType(draftData.victoryType);
      setTaskResults(draftData.taskResults);
      setMvpPlayer(draftData.mvpPlayer);
      setPlayerInfos(draftData.playerInfos || []);
      setGameDate(draftData.gameDate);
      setGameNumber(draftData.gameNumber);
      setGameDojoId(draftData.gameDojoId);
      setGameSeasonId(draftData.gameSeasonId);
      return true;
    }
    return false;
  };

  const clearDraft = () => {
    removeFromStorage(STORAGE_KEYS.GAME_RECORD_DRAFT);
  };

  const handleEditClick = (game: GameHistoryItem) => {
    setPendingEditGame(game);
    setShowEditConfirm(true);
  };

  const handleCopyEditClick = async (game: GameHistoryItem) => {
    // 复制当前记录数据，但不设置 editingGameId，这样保存时会创建新记录
    setPlayerCount(game.player_count);
    setWinningFaction(game.winning_faction as Faction);
    setVictoryType(game.victory_type as VictoryType);
    setTaskResults((game.task_results as unknown as boolean[] | null) || [null, null, null, null, null]);
    setMvpPlayer(game.good_mvp_player || game.evil_mvp_player || '');
    setGameDojoId(game.dojo_id ? String(game.dojo_id) : '');
    setGameSeasonId(game.season_id ? String(game.season_id) : '');

    // 设置日期为今天
    const today = new Date();
    setGameDate(today.toISOString().split('T')[0]);

    // 计算今天的局数，设置新局号
    const todayGames = historyGames.filter(g => g.game_date === today.toISOString().split('T')[0]);
    const maxGameNumber = todayGames.length > 0
      ? Math.max(...todayGames.map(g => g.game_number || 1))
      : 0;
    setGameNumber(maxGameNumber + 1);

    // 复制玩家信息
    let newPlayerInfos: PlayerInfo[] = [];
    if (game.players) {
      const goodCount = getGoodCount(game.player_count);
      for (let i = 1; i <= game.player_count; i++) {
        const player = game.players.find(p => p.player_number === i);
        let faction: Faction;
        if (player?.faction) {
          faction = player.faction as Faction;
        } else if (player?.player_role) {
          faction = ROLE_FACTION[player.player_role as PlayerRole] || (i <= goodCount ? 'good' : 'evil');
        } else {
          faction = i <= goodCount ? 'good' : 'evil';
        }
        newPlayerInfos.push({
          player_number: i,
          player_nickname: player?.player_nickname || '',
          player_role: (player?.player_role as PlayerRole) || '',
          faction,
        });
      }
      setPlayerInfos(newPlayerInfos);
    }

    // 不设置 editingGameId，这样保存时会创建新记录
    setEditingGameId(null);
    
    // V146: 等待积分计算完成后再打开弹窗
    // 手动计算积分
    try {
      const calculator = new StatsCalculator(game.player_count);
      const scores = await calculator.calculateGameScoresAsync(
        newPlayerInfos,
        game.winning_faction as Faction,
        game.victory_type as VictoryType,
        game.good_mvp_player || game.evil_mvp_player || ''
      );
      setCalculatedScores(scores);
    } catch (err) {
      console.error('[V146] 复制编辑时计算积分失败:', err);
    }
    
    setShowAddModal(true);
  };

  const confirmEditGame = async () => {
    if (!pendingEditGame) return;

    // V104 FIX: 关闭编辑确认弹窗
    setShowEditConfirm(false);
    setPendingEditGame(null);

    const game = pendingEditGame;
    setEditingGameId(game.id);
    setPlayerCount(game.player_count);
    setWinningFaction(game.winning_faction as Faction);
    setVictoryType(game.victory_type as VictoryType);
    setTaskResults((game.task_results as unknown as boolean[] | null) || [null, null, null, null, null]);
    setMvpPlayer(game.good_mvp_player || game.evil_mvp_player || '');
    setGameDojoId(game.dojo_id ? String(game.dojo_id) : '');
    setGameSeasonId(game.season_id ? String(game.season_id) : '');

    if (game.game_date) {
      setGameDate(game.game_date);
    } else {
      const today = new Date();
      setGameDate(today.toISOString().split('T')[0]);
    }

    if (game.players) {
      const goodCount = getGoodCount(game.player_count);
      const infos: PlayerInfo[] = [];
      for (let i = 1; i <= game.player_count; i++) {
        const player = game.players.find(p => p.player_number === i);
        let faction: Faction;
        if (player?.faction) {
          faction = player.faction as Faction;
        } else if (player?.player_role) {
          faction = ROLE_FACTION[player.player_role as PlayerRole] || (i <= goodCount ? 'good' : 'evil');
        } else {
          faction = i <= goodCount ? 'good' : 'evil';
        }
        infos.push({
          player_number: i,
          player_nickname: player?.player_nickname || '',
          player_role: (player?.player_role as PlayerRole) || '',
          faction,
        });
      }
      setPlayerInfos(infos);
    }

    setGameNumber(game.game_number || 1);
    setShowAddModal(true);
  };

  const filteredHistory = useMemo(() => {
    return historyGames.filter(game => {
      if (historyFilter !== 'all' && game.winning_faction !== historyFilter) return false;
      if (selectedPlayers && selectedPlayers.length > 0 && selectedPlayers[0]) {
        const searchText = selectedPlayers[0].toLowerCase();
        const playerMatch = game.players?.some((p: any) =>
          p.player_nickname?.toLowerCase().includes(searchText)
        );

        if (!playerMatch) return false;
      }
      return true;
    });
  }, [historyGames, historyFilter, selectedPlayers]);

  // V544-P1: 辅助函数 - 获取记录的时间戳
  const getRecordTimestamp = (record: { type: 'game'; data: GameHistoryItem } | { type: 'penalty'; data: PenaltyRecord }): number => {
    if (record.type === 'game') {
      return new Date(record.data.game_date || record.data.created_at || '').getTime();
    } else {
      return new Date(record.data.penalty_date).getTime();
    }
  };

  // V544-P1: 辅助函数 - 归并两个已排序数组（降序）
  const mergeSortedRecords = (
    games: Array<{ type: 'game'; data: GameHistoryItem }>,
    penalties: Array<{ type: 'penalty'; data: PenaltyRecord }>
  ): Array<{ type: 'game'; data: GameHistoryItem } | { type: 'penalty'; data: PenaltyRecord }> => {
    const result: Array<{ type: 'game'; data: GameHistoryItem } | { type: 'penalty'; data: PenaltyRecord }> = [];
    let i = 0, j = 0;
    
    // 归并两个有序数组（O(n) 复杂度）
    while (i < games.length && j < penalties.length) {
      const timeA = getRecordTimestamp(games[i]);
      const timeB = getRecordTimestamp(penalties[j]);
      
      if (timeA >= timeB) {
        result.push(games[i]);
        i++;
      } else {
        result.push(penalties[j]);
        j++;
      }
    }
    
    // 添加剩余元素
    while (i < games.length) {
      result.push(games[i]);
      i++;
    }
    while (j < penalties.length) {
      result.push(penalties[j]);
      j++;
    }
    
    return result;
  };

  // 合并游戏记录和扣分记录，按时间顺序排列
  const mergedHistoryRecords = useMemo(() => {
    let records: Array<{ type: 'game'; data: GameHistoryItem } | { type: 'penalty'; data: PenaltyRecord }> = [];
    
    // 根据筛选条件过滤扣分记录
    const filteredPenalties = penaltyRecords.filter(record => {
      if (historyFilter === 'violation') return true;
      if (historyFilter !== 'all') return false;
      if (selectedPlayers && selectedPlayers.length > 0 && selectedPlayers[0]) {
        const searchText = selectedPlayers[0].toLowerCase();
        const nicknameMatch = record.player_nickname?.toLowerCase().includes(searchText);
        if (!nicknameMatch) return false;
      }
      return true;
    });
    
    // 如果只显示违规记录，只返回扣分记录
    if (historyFilter === 'violation') {
      return filteredPenalties.map(record => ({ type: 'penalty' as const, data: record }));
    }
    
    // 合并游戏记录和扣分记录（只在 all 模式下显示）
    if (historyFilter === 'all') {
      // V544-P1: 预处理：确保两个数组都已按时间降序排序
      const sortedGames = [...filteredHistory]
        .map(game => ({ type: 'game' as const, data: game }))
        .sort((a, b) => getRecordTimestamp(b) - getRecordTimestamp(a));
      
      const sortedPenalties = [...filteredPenalties]
        .map(record => ({ type: 'penalty' as const, data: record }))
        .sort((a, b) => getRecordTimestamp(b) - getRecordTimestamp(a));
      
      // V544-P1: 使用归并排序替代全量排序（O(n) vs O(n log n)）
      records = mergeSortedRecords(sortedGames, sortedPenalties);
    } else {
      // good 或 evil 模式，只显示游戏记录
      records = filteredHistory.map(game => ({ type: 'game' as const, data: game }));
    }
    
    return records;
  }, [filteredHistory, penaltyRecords, historyFilter, selectedPlayers]);

  // V539/V543: 禁用虚拟滚动，历史记录列表包含不同类型卡片（游戏记录/违规记录），高度不一致，直接渲染确保间距统一

  // V542: 创建 memoized 查找表，避免每次渲染时遍历数组
  const dojoMap = useMemo(() => {
    const map = new Map<string | number, DojosRow>();
    dojos.forEach(d => map.set(d.id, d));
    return map;
  }, [dojos]);

  const seasonMap = useMemo(() => {
    const map = new Map<string | number, SeasonsRow>();
    seasons.forEach(s => map.set(s.id, s));
    return map;
  }, [seasons]);

  // 清除按钮
  const handleClearSearch = () => {
    setSelectedPlayers([]);
    setAllPlayerOptions([]);
  };

  // V528: 渲染历史记录项的辅助函数（用于虚拟滚动）
  const renderHistoryRecord = (record: { type: 'game'; data: GameHistoryItem } | { type: 'penalty'; data: PenaltyRecord }, idx: number) => {
    if (record.type === 'penalty') {
      const penaltyData = record.data;
      const recordDate = new Date(penaltyData.penalty_date).toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' });
      const dojo = penaltyData.dojo_id != null ? dojoMap.get(penaltyData.dojo_id) : undefined;
      const season = penaltyData.season_id != null ? seasonMap.get(penaltyData.season_id) : undefined;
      
      return (
        <div
          key={penaltyData.id}
          className="avalon-card overflow-hidden border-l-4 border-l-amber-500 py-3 px-3"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="flex flex-col items-center justify-center px-2 py-1 rounded bg-amber-500/20 text-amber-400">
                <span className="text-xs opacity-80">{recordDate}</span>
                <AlertTriangle className="w-3 h-3" />
              </div>
              <div>
                <div className="text-white font-medium text-sm">
                  {penaltyData.player_nickname} · {PENALTY_NAMES[penaltyData.penalty_type as keyof typeof PENALTY_NAMES]}
                </div>
                {(dojo || season) && (
                  <div className="text-white font-bold text-xs" style={{ textShadow: '0 0 5px rgba(255,255,255,0.5)' }}>
                    {dojo && season ? `${dojo.name} - ${season.name}` : dojo?.name || season?.name}
                  </div>
                )}
                {penaltyData.reason && (
                  <div className="text-avalon-text text-xs mt-1">{penaltyData.reason}</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-amber-500 font-bold text-lg">
                {penaltyData.penalty_score}分
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleEditPenaltyRecord(penaltyData); }}
                className="p-1 hover:bg-amber-500/20 rounded"
                title="编辑"
              >
                <Edit className="w-4 h-4 text-amber-400" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); penaltyData.id && handleDeletePenaltyRecord(penaltyData.id); }}
                className="p-1 hover:bg-red-500/20 rounded"
                title="删除"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </div>
          </div>
        </div>
      );
    } else {
      const game = record.data;
      const gameDateStr = game.game_date || new Date(game.created_at || '').toISOString().split('T')[0];
      const gameDate = new Date(gameDateStr).toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' });
      const isGoodWin = game.winning_faction === 'good';
      const dojo = game.dojo_id != null ? dojoMap.get(game.dojo_id) : undefined;
      const season = game.season_id != null ? seasonMap.get(game.season_id) : undefined;

      return (
        <div
          key={game.id}
          className={`avalon-card overflow-hidden border-l-4 py-3 px-3 ${
            isGoodWin ? 'border-l-blue-500' : 'border-l-red-500'
          }`}
        >
          <div
            className="cursor-pointer"
            onClick={() => {
              const isExpanding = expandedGameId !== game.id;
              if (isExpanding) {
                // V546: 展开时懒加载玩家数据
                loadGamePlayersIfNeeded(game.id);
              }
              setExpandedGameId(isExpanding ? game.id : null);
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <div className={`flex flex-col items-center justify-center px-2 py-1 rounded ${
                  isGoodWin ? 'bg-blue-500/20 text-blue-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  <span className="text-xs opacity-80">{gameDate}</span>
                  <span className="text-xs font-bold">第{game.game_number}局</span>
                </div>
                <div>
                  <div className="text-white font-medium text-sm">
                    {game.player_count}人局 · {isGoodWin ? '正义' : '邪恶'}胜利
                  </div>
                  {(dojo || season) && (
                    <div className="text-white font-bold text-xs" style={{ textShadow: '0 0 5px rgba(255,255,255,0.5)' }}>
                      {dojo && season ? `${dojo.name} - ${season.name}` : dojo?.name || season?.name}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyEditClick(game);
                }}
                className="p-1.5 rounded-lg bg-avalon-card text-avalon-text hover:text-avalon-gold transition-colors"
                title="复制编辑"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(game);
                }}
                className="p-1.5 rounded-lg bg-avalon-card text-avalon-text hover:text-white transition-colors"
                title="编辑"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
              {expandedGameId === game.id ? <ChevronUp className="w-4 h-4 text-avalon-text" /> : <ChevronDown className="w-4 h-4 text-avalon-text" />}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs">
            <span className="text-avalon-text">
              任务：{game.mission_summary || '未记录'}
            </span>
            <span className="text-avalon-gold font-medium">
              {getVictoryTypeLabel(game.victory_type)}
            </span>
          </div>
        </div>

        {expandedGameId === game.id && (
          <div className="border-t border-avalon-card/50 mt-2 pt-2 bg-avalon-bg-dark/30">
            {/* V546: 显示加载状态 */}
            {loadingPlayerGames.has(game.id) ? (
              <div className="flex items-center justify-center py-4">
                <RefreshCw className="w-5 h-5 text-avalon-gold animate-spin" />
                <span className="ml-2 text-sm text-avalon-text">正在加载玩家信息...</span>
              </div>
            ) : game.players && game.players.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5 mb-2">
              {game.players.map((player) => {
                const actualFaction = player.player_role ? ROLE_FACTION[player.player_role as PlayerRole] : player.faction;
                const isGood = actualFaction === 'good';
                const isWinner = game.winning_faction === actualFaction;
                const isInTaskTeam = game.task_team?.includes(player.player_number) || false;
                const isMVP = player.is_mvp || false;
                
                let cardClasses = 'p-2 rounded text-xs relative border ';
                
                const isOnlyTaskTeamMember = isInTaskTeam && !isMVP;
                const isOnlyLeader = isMVP && !isInTaskTeam;
                const isBothLeaderAndMember = isInTaskTeam && isMVP;
                
                if (isBothLeaderAndMember) {
                  cardClasses += 'bg-purple-500/10 border-purple-500/50 ring-1 ring-avalon-gold ';
                } else if (isOnlyTaskTeamMember) {
                  cardClasses += 'bg-purple-500/10 border-purple-500/50 ';
                } else if (isOnlyLeader) {
                  cardClasses += isGood
                    ? 'bg-blue-500/10 border-blue-500/50 ring-1 ring-avalon-gold '
                    : 'bg-red-500/10 border-red-500/50 ring-1 ring-avalon-gold ';
                } else {
                  cardClasses += isGood
                    ? 'bg-blue-500/10 border-blue-500/50 '
                    : 'bg-red-500/10 border-red-500/50 ';
                }

                return (
                  <div
                    key={player.player_number}
                    className={cardClasses}
                  >
                    {isMVP && (
                      <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-avalon-gold border-2 border-avalon-bg-dark flex items-center justify-center z-10 shadow-lg">
                        <Crown className="w-2.5 h-2.5 text-avalon-bg-dark" />
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        {player.player_nickname ? (
                          <span className="text-white font-medium truncate max-w-[60px]">{player.player_nickname}</span>
                        ) : (
                          <span className="text-white font-medium">{player.player_number}号</span>
                        )}
                      </div>
                      <div className={`font-bold text-sm ${isWinner ? 'text-green-400' : 'text-red-400'}`}>
                        {isWinner ? '+' : ''}{player.score_change}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      {player.player_role && (
                        <span className={`text-xs font-medium ${
                          isGood ? 'text-blue-400' : 'text-red-400'
                        }`}>
                          {ROLE_NAMES[player.player_role as PlayerRole]}
                        </span>
                      )}
                      <span className={`text-xs px-1 py-0.5 rounded ${
                        isGood ? 'bg-blue-500/20 text-blue-300' : 'bg-red-500/20 text-red-300'
                      }`}>
                        {isGood ? '正义' : '邪恶'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {game.task_results && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-avalon-text">任务进程:</span>
                <div className="flex gap-1">
                  {(game.task_results as unknown as (boolean | null)[]).map((result, idx) => (
                    <div
                      key={idx}
                      className={`w-6 h-6 rounded flex items-center justify-center text-xs ${
                        result === true ? 'bg-blue-500 text-white' :
                        result === false ? 'bg-red-500 text-white' :
                        'bg-avalon-card text-avalon-text'
                      }`}
                    >
                      {result === true ? '✓' : result === false ? '✗' : idx + 1}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-avalon-card/50">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenCommentModal(game.id);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-avalon-purple/20 hover:bg-avalon-purple/30 border border-avalon-purple/40 text-avalon-gold font-medium transition-all text-sm"
              >
                <MessageSquare className="w-4 h-4" />
                <span>查看评论</span>
                {game.comments_count ? (
                  <span className="px-1.5 py-0.5 rounded-full bg-avalon-gold text-avalon-bg-dark text-xs font-bold">
                    {game.comments_count}
                  </span>
                ) : null}
              </button>
            </div>
              </>
            ) : null}
          </div>
        )}
      </div>
      );
    }
  };

  const getVictoryTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      mission: '完成任务梅林隐藏',
      assassin_fail: '提前刺杀失败',
      sabotage: '破坏三次任务',
      assassin_success: '刺杀梅林成功',
    };
    return labels[type] || type;
  };


  const fetchPlayerDebugDetails = async (playerNumber: number) => {
    try {
      const details = await statsDataService.fetchPlayerDebugDetails(playerNumber);
      setDebugPlayerDetails(details);
      return details;
    } catch (err) {
      console.error('[V100] 获取调试数据失败:', err);
      return [];
    }
  };

  const openDataResetModal = () => {
    const lockCheck = resetService.checkLock(resetState);
    if (lockCheck.isLocked) {
      alert(`密码错误次数过多，请 ${lockCheck.remainingMinutes} 分钟后重试`);
      return;
    }

    setResetState(resetService.resetLock());
    setResetPreviewCount(0);
    setShowDataResetModal(true);
  };

  const calculateResetPreview = async () => {
    const count = await resetService.calculatePreviewCount(resetState.selectedDojos, resetState.selectedSeasons);
    setResetPreviewCount(count);
  };

  useEffect(() => {
    if (showDataResetModal) {
      calculateResetPreview();
    }
  }, [resetState.selectedDojos, resetState.selectedSeasons, showDataResetModal]);

  const handleDojoSelection = (dojoId: string) => {
    setResetState(prev => ({
      ...prev,
      selectedDojos: resetService.toggleDojoSelection(prev.selectedDojos, dojoId),
    }));
  };

  const handleSeasonSelection = (seasonId: string) => {
    setResetState(prev => ({
      ...prev,
      selectedSeasons: resetService.toggleSeasonSelection(prev.selectedSeasons, seasonId),
    }));
  };

  const handleResetPasswordSubmit = async () => {
    if (!resetState.passwordInput) return;

    if (resetService.validatePassword(resetState.passwordInput)) {
      try {
        await resetService.executeReset(resetState.selectedDojos, resetState.selectedSeasons);
        setShowDataResetModal(false);
        setResetState(resetService.resetLock());
        await loadPlayerStats();
        await loadHistoryGames();
        alert(`成功删除战绩记录`);
      } catch (err) {
        alert('删除失败');
      }
    } else {
      const newState = resetService.incrementAttempt(resetState);
      setResetState(newState);

      if (newState.lockedUntil) {
        setShowDataResetModal(false);
        alert('密码错误次数过多，请 5 分钟后重试');
      }
    }
  };

  return (
    <ThemedBackground pageName="game-stats">
    <div className="min-h-screen p-4 pt-[72px]">
      {/* V189: 固定头部 */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3" style={{ backgroundColor: '#1A0F2A', borderBottom: '1px solid #5A4BFF35' }}>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-gold hover:bg-avalon-purple transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-white">战绩统计</h1>
        </div>
        <div className="flex items-center gap-2">
          {canResetStats && (
            <button
              onClick={openDataResetModal}
              className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-text hover:text-white hover:bg-red-500/50 transition-colors"
              title="重置战绩数据"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
          {/* V156: 腾讯同步按钮已移入导入导出弹窗 */}
          <button
            onClick={() => setShowExportImportModal(true)}
            className="w-8 h-8 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-text hover:text-white hover:bg-avalon-purple/50 transition-colors"
            title="导入导出数据"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowDojoSeasonModal(true)}
            className="w-8 h-8 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-text hover:text-white hover:bg-avalon-purple/50 transition-colors"
            title="管理道场和赛季"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              const todayGames = historyGames.filter(g => g.game_date === today);
              const maxGameNumber = todayGames.length > 0
                ? Math.max(...todayGames.map(g => g.game_number || 1))
                : 0;
              
              const hasDraft = loadDraft();
              if (!hasDraft) {
                setEditingGameId(null);
                setCalculatedScores(null);
                setGameDojoId('');
                setGameSeasonId('');
                setGameNumber(maxGameNumber + 1);
                setGameDate(today);
              } else {
                console.log('[V211] 加载到草稿数据，保留草稿内容');
              }
              
              setShowAddModal(true);
            }}
            className="px-4 h-8 rounded-lg bg-avalon-gold text-avalon-bg-dark font-medium flex items-center justify-center gap-1.5 hover:bg-yellow-400 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>录入</span>
          </button>
        </div>
      </div>



      <div className="flex gap-2 mb-4">
        {[
          { key: 'overview', label: '总览', icon: TrendingUp },
          { key: 'leaderboard', label: '排行榜', icon: Trophy },
          { key: 'history', label: '记录', icon: Medal },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key as typeof activeTab)}
            className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${
              activeTab === key
                ? 'bg-avalon-purple text-white'
                : 'bg-avalon-card text-avalon-text hover:text-white'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span className="text-sm font-medium">{label}</span>
          </button>
        ))}
      </div>

      <div className="mb-4">
        <TimeFilter 
          onFilterChange={handleTimeFilter}
          seasons={seasons.map(s => ({ id: String(s.id), name: s.name }))}
          venues={dojos.map(d => ({ id: String(d.id), name: d.name }))}
          onSeasonChange={handleSeasonChange}
          onVenueChange={handleDojoChange}
        />
      </div>
      {(activeTab === 'overview' || activeTab === 'leaderboard') && (
        <div className="mb-4 relative">
          <div className="w-full relative">
            <input
              type="text"
              value={selectedPlayers[0] || ''}
              onChange={(e) => {
                const searchText = e.target.value;
                if (!searchText.trim()) {
                  setSelectedPlayers([]);
                  setAllPlayerOptions([]);
                  return;
                }
                // 实时更新搜索关键词，用于下拉选项显示
                setSelectedPlayers([searchText]);
              }}
              onFocus={async () => {
                const searchText = selectedPlayers[0];
                if (searchText && searchText.trim()) {
                  try {
                    const { data, error } = await supabase
                      .from('game_players')
                      .select('player_nickname')
                      .ilike('player_nickname', `%${searchText}%`)
                      .limit(10);
                    
                    if (error) throw error;
                    
                    const uniqueNicknames = Array.from(new Set(data?.map(p => p.player_nickname).filter(Boolean) as string[]));
                    setAllPlayerOptions(uniqueNicknames.map(name => ({ value: name, label: name })));
                  } catch (err) {
                    console.error('搜索玩家昵称失败:', err);
                    setAllPlayerOptions([]);
                  }
                }
              }}
              placeholder="搜索昵称..."
              className="w-full px-4 py-2 pl-10 pr-10 rounded-lg bg-avalon-card text-white border border-avalon-purple/30 focus:border-avalon-purple outline-none text-sm"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-avalon-text pointer-events-none" />
            {selectedPlayers[0] && (
              <button
                onClick={handleClearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-avalon-text hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            
            {/* 搜索下拉选项 */}
            {allPlayerOptions.length > 0 && selectedPlayers[0] && (
              <div className="absolute z-50 w-full mt-1 bg-avalon-card border border-avalon-purple/30 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {allPlayerOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSelectedPlayers([option.value]);
                      setAllPlayerOptions([]);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-white hover:bg-avalon-purple/30 transition-colors first:rounded-t-lg last:rounded-b-lg"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'overview' && (
        <div className="space-y-4">
          {loadingStates.overview ? (
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="avalon-card p-3">
                  <div className="h-4 bg-avalon-card rounded w-20 mb-2 animate-pulse"></div>
                  <div className="h-8 bg-avalon-card rounded w-full animate-pulse"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <div className="avalon-card p-3">
                <div className="flex items-center gap-1.5 text-avalon-text text-xs mb-1">
                  <Users className="w-3.5 h-3.5" />
                  <span>总场次</span>
                </div>
                <div className="text-xl font-bold text-white">{totalGames}</div>
              </div>
              <div className="avalon-card p-3">
                <div className="flex items-center gap-1.5 text-blue-400 text-xs mb-1">
                  <Trophy className="w-3.5 h-3.5" />
                  <span>正义阵营</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-blue-400">{factionWins.good}</span>
                  <span className="text-xs text-blue-400/80">
                    {totalGames > 0 ? Math.round((factionWins.good / totalGames) * 100) : 0}%胜率
                  </span>
                </div>
              </div>
              <div className="avalon-card p-3">
                <div className="flex items-center gap-1.5 text-red-400 text-xs mb-1">
                  <Trophy className="w-3.5 h-3.5" />
                  <span>邪恶阵营</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-red-400">{factionWins.evil}</span>
                  <span className="text-xs text-red-400/80">
                    {totalGames > 0 ? Math.round((factionWins.evil / totalGames) * 100) : 0}%胜率
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="avalon-card">
            <div className="flex items-center justify-between mb-4">
              <h3 id="stats-title" className="text-white font-semibold flex items-center gap-2">
                <Trophy className="w-5 h-5 text-avalon-gold" />
                玩家积分榜
                {debugMode && (
                  <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 text-xs">
                    调试模式
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                {debugMode && (
                  <>
                    <button
                      onClick={async () => {
                        alert('触发器方案已启用，统计数据将自动更新，无需手动重算');
                        await loadPlayerStats();
                      }}
                      disabled={isLoading}
                      className="px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/40 transition-colors disabled:opacity-50 text-xs"
                    >
                      刷新统计
                    </button>
                    <button
                      onClick={() => setShowDataAuditModal(true)}
                      className="px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/40 transition-colors text-xs"
                    >
                      数据审计
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowRulesModal(true)}
                  className="px-3 py-2 rounded-lg bg-avalon-card text-avalon-text flex items-center gap-2 hover:text-white hover:bg-avalon-purple/50 transition-colors"
                >
                  <HelpCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium whitespace-nowrap">积分规则</span>
                </button>
                {/* V104: 移除冗余的强制刷新按钮 */}
                <button
                  onClick={async () => {
                    await loadOverviewData();
                    await loadHistoryData();
                    await loadPenaltyData();
                  }}
                  disabled={loadingStates.overview}
                  className="p-2 rounded-lg bg-avalon-card text-avalon-text hover:text-white hover:bg-avalon-purple/50 transition-colors disabled:opacity-50"
                  title="刷新数据"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingStates.overview ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            {loadingStates.overview ? (
              <div className="text-center py-8 text-avalon-text">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-avalon-card rounded w-3/4 mx-auto"></div>
                  <div className="h-4 bg-avalon-card rounded w-1/2 mx-auto"></div>
                  <div className="h-4 bg-avalon-card rounded w-2/3 mx-auto"></div>
                </div>
              </div>
            ) : playerStats.length === 0 ? (
              <div className="text-center py-8 text-avalon-text">
                <p>暂无战绩数据</p>
                <p className="text-sm mt-2">点击右上角录入本局结果</p>
              </div>
            ) : filteredPlayerStats.length === 0 ? (
              <div className="text-center py-8 text-avalon-text">
                <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>未找到匹配的玩家</p>
              </div>
            ) : (
              <div className="space-y-2">
                {playerScoreLeaderboardData.slice(0, isOverviewExpanded ? DISPLAY_COUNT_EXPANDED : DISPLAY_COUNT_DEFAULT).map((player, index) => (
                  <div
                    key={`${player.player_name}_${index}`}
                    className={`group relative flex items-center justify-between p-3 rounded-xl transition-all duration-300 hover:scale-[1.02] ${
                      index === 0 
                        ? 'bg-gradient-to-br from-yellow-500/30 via-yellow-500/15 to-transparent border border-yellow-500/50 shadow-lg shadow-yellow-500/20' 
                        : index === 1 
                          ? 'bg-gradient-to-br from-slate-400/30 via-slate-400/15 to-transparent border border-slate-400/50 shadow-lg shadow-slate-400/20'
                          : index === 2
                            ? 'bg-gradient-to-br from-amber-600/30 via-amber-600/15 to-transparent border border-amber-600/50 shadow-lg shadow-amber-600/20'
                            : 'bg-avalon-bg-dark/50 hover:bg-avalon-bg-dark/70 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold shadow-lg ${
                          index === 0 
                            ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' 
                            : index === 1 
                              ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-white'
                              : index === 2
                                ? 'bg-gradient-to-br from-amber-500 to-amber-700 text-white'
                                : 'bg-avalon-card text-avalon-text'
                        }`}>
                          {index + 1}
                        </div>
                      </div>
                      <div>
                        {/* V145: 导航到玩家详情页 - 使用nickname作为参数 */}
                        <div 
                          className="text-white font-medium cursor-pointer hover:text-avalon-gold transition-colors"
                          onClick={() => {
                            console.log('[V145] 导航到玩家详情页(排行榜):', {
                              clickedPlayer: player.player_name,
                              playerNumber: player.player_number,
                              navigateTo: `/player/${player.player_number}?nickname=${encodeURIComponent(player.player_name)}`,
                              timestamp: new Date().toISOString(),
                            });
                            navigate(`/player/${player.player_number}?nickname=${encodeURIComponent(player.player_name)}`);
                          }}
                        >
                          {player.player_name}
                          {/* V111: 如果昵称包含#号（同名区分），显示编号提示 */}
                          {player.player_name.includes('#') && (
                            <span className="text-xs text-avalon-text ml-1">
                              ({player.player_name.split('#')[1]}号)
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-avalon-text">
                          {/* V122: 增加胜率显示 */}
                          {player.total_games > 0 ? `${player.win_rate}%胜率 | ` : '--%胜率 | '}
                          {player.wins}胜 {player.losses}负<br/>{player.mvp_count}次 MVP
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      {/* V154: 详情按钮入口 - 使用nickname参数 */}
                      <button
                        onClick={() => {
                          console.log('[V154] 导航到玩家详情页:', {
                            clickedPlayer: player.player_name,
                            playerNumber: player.player_number,
                            navigateTo: `/player/${player.player_number}?nickname=${encodeURIComponent(player.player_name)}`,
                            timestamp: new Date().toISOString(),
                          });
                          navigate(`/player/${player.player_number}?nickname=${encodeURIComponent(player.player_name)}`);
                        }}
                        className="px-2 py-1 rounded bg-avalon-card flex items-center justify-center text-avalon-gold hover:text-white hover:bg-avalon-purple/50 transition-colors"
                        title="查看详情"
                      >
                        <span className="text-[10px]">详情</span>
                      </button>
                      <div>
                        <div className={`font-bold ${player.total_score >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {player.total_score > 0 ? '+' : ''}{player.total_score}
                        </div>
                        <div className="text-xs text-avalon-text">
                          {player.total_games}局
                        </div>
                      </div>
                      {debugMode && (
                        <button
                          onClick={() => {
                            setDebugTargetPlayer(player);
                            fetchPlayerDebugDetails(player.player_number);
                            setShowDebugModal(true);
                          }}
                          className="px-3 py-1.5 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/40"
                        >
                          <HelpCircle className="w-4 h-4 flex-shrink-0" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {filteredPlayerStats.length > DISPLAY_COUNT_DEFAULT && (
                  <button
                    onClick={() => setIsOverviewExpanded(!isOverviewExpanded)}
                    className="w-full py-2 px-4 mt-2 flex items-center justify-center gap-2 text-sm text-avalon-text hover:text-white bg-avalon-card/50 hover:bg-avalon-card rounded-lg transition-all"
                  >
                    {isOverviewExpanded ? '⬆️ 收起' : `⬇️ 展开查看前 ${Math.min(DISPLAY_COUNT_EXPANDED, filteredPlayerStats.length)} 名`}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'leaderboard' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            {[
              { key: 'score', label: '个人胜率', icon: Trophy },
              { key: 'faction', label: '阵营胜率', icon: Shield },
              { key: 'mvp', label: 'MVP荣誉', icon: Crown },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => {
                  if (key === 'faction') {
                    if (leaderboardType === 'faction') {
                      setFactionSortMode(prev => prev === 'good' ? 'evil' : 'good');
                    } else {
                      setLeaderboardType('faction');
                    }
                  } else {
                    setLeaderboardType(key as LeaderboardType);
                  }
                }}
                className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-colors text-sm ${
                  leaderboardType === key
                    ? key === 'faction'
                      ? factionSortMode === 'good'
                        ? 'bg-blue-500/30 text-blue-400 border border-blue-500/50'
                        : 'bg-red-500/30 text-red-400 border border-red-500/50'
                      : 'bg-avalon-purple text-white'
                    : 'bg-avalon-card text-avalon-text hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            {[
              { key: 'percival', label: '派西排行', emoji: '🛡️' },
              { key: 'merlin', label: '梅林排行', emoji: '👑' },
              { key: 'assassin', label: '刺客排行', emoji: '🗡️' },
            ].map(({ key, label, emoji }) => (
              <button
                key={key}
                onClick={() => setLeaderboardType(key as LeaderboardType)}
                className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-colors text-sm ${
                  leaderboardType === key
                    ? 'bg-avalon-purple text-white'
                    : 'bg-avalon-card text-avalon-text hover:text-white'
                }`}
              >
                <span>{emoji}</span>
                <span>{label}</span>
              </button>
            ))}
          </div>

          {leaderboardType === 'faction' && (
            <div className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg transition-all text-sm ${
              factionSortMode === 'good'
                ? 'bg-blue-500/20 text-blue-400'
                : 'bg-red-500/20 text-red-400'
            }`}>
              <Shield className="w-4 h-4" />
              <span className="font-medium">
                {factionSortMode === 'good' ? '正义阵营胜率排行' : '邪恶阵营胜率排行'}
              </span>
            </div>
          )}

          <div className="avalon-card">
            {/* V122: 排行榜上榜门槛提示 */}
            {leaderboardType === 'score' && (
              <div className="text-xs text-avalon-text mb-3 px-1">
                上榜要求：≥10场
              </div>
            )}
            <div className="space-y-2">
              {sortedLeaderboardData.slice(0, isLeaderboardExpanded ? DISPLAY_COUNT_EXPANDED : DISPLAY_COUNT_DEFAULT).map((player, index) => (
                <div
                  key={`${player.player_name}_${index}`}
                  onClick={() => {
                    // V142: 增强导航日志追踪 - 积分榜玩家跳转
                    console.log('[V142] 导航到玩家详情页(积分榜):', {
                      clickedPlayer: player.player_name,
                      playerNumber: player.player_number,
                      navigateTo: `/player/${player.player_number}`,
                      timestamp: new Date().toISOString(),
                    });
                    navigate(`/player/${player.player_number}?nickname=${encodeURIComponent(player.player_name)}`);
                  }}
                  className={`group relative flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-300 hover:scale-[1.02] ${
                    index === 0 
                      ? 'bg-gradient-to-br from-yellow-500/30 via-yellow-500/15 to-transparent border border-yellow-500/50 shadow-lg shadow-yellow-500/20' 
                      : index === 1 
                        ? 'bg-gradient-to-br from-slate-400/30 via-slate-400/15 to-transparent border border-slate-400/50 shadow-lg shadow-slate-400/20'
                        : index === 2
                          ? 'bg-gradient-to-br from-amber-600/30 via-amber-600/15 to-transparent border border-amber-600/50 shadow-lg shadow-amber-600/20'
                          : 'bg-avalon-bg-dark/50 hover:bg-avalon-bg-dark/70 border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold shadow-lg ${
                        index === 0 
                          ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' 
                          : index === 1 
                            ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-white'
                            : index === 2
                              ? 'bg-gradient-to-br from-amber-500 to-amber-700 text-white'
                              : 'bg-avalon-card text-avalon-text'
                      }`}>
                        {index + 1}
                      </div>
                    </div>
                    <div>
                      <div className="text-white font-medium">
                        {player.player_name}
                        {player.player_name.includes('#') && (
                          <span className="text-xs text-avalon-text ml-1">
                            ({player.player_name.split('#')[1]}号)
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-avalon-text">
                        {/* V122: 根据排行榜类型显示不同信息 */}
                        {(() => {
                          switch (leaderboardType) {
                            case 'score':
                              const wins = Math.round((player.win_rate || 0) * (player.total_games || 0) / 100);
                              const losses = (player.total_games || 0) - wins;
                              return (
                                <>
                                  <div>{player.win_rate}%胜率 | {wins}胜 {losses}负</div>
                                  <div>{player.mvp_count}次 MVP</div>
                                </>
                              );
                            case 'faction':
                              const factionRate = (player as any).factionWinRate;
                              return `${factionRate}%${factionSortMode === 'good' ? '正义' : '邪恶'}胜率 · ${player.total_games}局`;
                            case 'mvp':
                              return (
                                <>
                                  <div>{player.mvp_count}次 MVP</div>
                                  <div>{player.total_games}局</div>
                                </>
                              );
                            case 'percival':
                              const percivalRate = (player as any).roleWinRate;
                              return `${percivalRate}%胜率 · ${player.percival_wins}/${player.percival_games}局`;
                            case 'merlin':
                              const merlinRate = (player as any).roleWinRate;
                              return `${merlinRate}%胜率 · ${player.merlin_wins}/${player.merlin_games}局`;
                            case 'assassin':
                              const assassinRate = (player as any).roleWinRate;
                              return `${assassinRate}%成功率 · ${player.assassin_wins}/${player.assassin_games}局`;
                            default:
                              return `${player.total_games}局 · 胜率${player.win_rate}%`;
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    {/* V154: 详情按钮入口 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('[V154] 导航到玩家详情页:', {
                          clickedPlayer: player.player_name,
                          playerNumber: player.player_number,
                          navigateTo: `/player/${player.player_number}?nickname=${encodeURIComponent(player.player_name)}`,
                          timestamp: new Date().toISOString(),
                        });
                        navigate(`/player/${player.player_number}?nickname=${encodeURIComponent(player.player_name)}`);
                      }}
                      className="px-2 py-1 rounded bg-avalon-card flex items-center justify-center text-avalon-gold hover:text-white hover:bg-avalon-purple/50 transition-colors"
                      title="查看详情"
                    >
                      <span className="text-[10px]">详情</span>
                    </button>
                    <div className={`font-bold ${
                      leaderboardType === 'score' ? 'text-green-400' :
                      leaderboardType === 'faction' ? 'text-green-400' :
                      leaderboardType === 'mvp' ? 'text-avalon-gold' :
                      leaderboardType === 'percival' || leaderboardType === 'merlin' || leaderboardType === 'assassin' ? 'text-green-400' :
                      player.total_score >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {/* V122: 根据排行榜类型显示不同数值 */}
                      {(() => {
                        switch (leaderboardType) {
                          case 'score':
                            return `${player.win_rate}%胜率`;
                          case 'faction':
                            return `${(player as any).factionWinRate}%`;
                          case 'mvp':
                            return `${player.mvp_count}次`;
                          case 'percival':
                          case 'merlin':
                          case 'assassin':
                            return `${(player as any).roleWinRate}%`;
                          default:
                            return `${player.win_rate}%胜率`;
                        }
                      })()}
                    </div>
                  </div>
                </div>
              ))}
              {sortedLeaderboardData.length > DISPLAY_COUNT_DEFAULT && (
                <button
                  onClick={() => setIsLeaderboardExpanded(!isLeaderboardExpanded)}
                  className="w-full py-2 px-4 mt-2 flex items-center justify-center gap-2 text-sm text-avalon-text hover:text-white bg-avalon-card/50 hover:bg-avalon-card rounded-lg transition-all"
                >
                  {isLeaderboardExpanded ? '⬆️ 收起' : `⬇️ 展开查看前 ${Math.min(DISPLAY_COUNT_EXPANDED, sortedLeaderboardData.length)} 名`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <>
          {loadingStates.history ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="avalon-card p-4 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-12 bg-avalon-card rounded"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-avalon-card rounded w-3/4"></div>
                      <div className="h-3 bg-avalon-card rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative w-full">
              <input
                type="text"
                value={selectedPlayers[0] || ''}
                onChange={(e) => {
                  const searchText = e.target.value;
                  if (!searchText.trim()) {
                    setSelectedPlayers([]);
                    setAllPlayerOptions([]);
                    return;
                  }
                  // 实时更新搜索关键词，用于下拉选项显示
                  setSelectedPlayers([searchText]);
                }}
                onFocus={async () => {
                  const searchText = selectedPlayers[0];
                  if (searchText && searchText.trim()) {
                    try {
                      const { data, error } = await supabase
                        .from('game_players')
                        .select('player_nickname')
                        .ilike('player_nickname', `%${searchText}%`)
                        .limit(10);
                      
                      if (error) throw error;
                      
                      const uniqueNicknames = Array.from(new Set(data?.map(p => p.player_nickname).filter(Boolean) as string[]));
                      setAllPlayerOptions(uniqueNicknames.map(name => ({ value: name, label: name })));
                    } catch (err) {
                      console.error('搜索玩家昵称失败:', err);
                      setAllPlayerOptions([]);
                    }
                  }
                }}
                placeholder="搜索昵称..."
                className="w-full px-4 py-2 pl-10 pr-10 rounded-lg bg-avalon-card text-white border border-avalon-purple/30 focus:border-avalon-purple outline-none text-sm"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-avalon-text pointer-events-none" />
              {selectedPlayers[0] && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-avalon-text hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              
              {/* 搜索下拉选项 */}
              {allPlayerOptions.length > 0 && selectedPlayers[0] && (
                <div className="absolute z-50 w-full mt-1 bg-avalon-card border border-avalon-purple/30 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {allPlayerOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedPlayers([option.value]);
                        setAllPlayerOptions([]);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-white hover:bg-avalon-purple/30 transition-colors first:rounded-t-lg last:rounded-b-lg"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {[
                { key: 'all', label: '全部' },
                { key: 'good', label: '正义' },
                { key: 'evil', label: '邪恶' },
                { key: 'violation', label: '违规' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setHistoryFilter(key as typeof historyFilter)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    historyFilter === key
                      ? key === 'all'
                        ? 'bg-avalon-purple text-white'
                        : key === 'good'
                        ? 'bg-blue-500/30 text-blue-400 border border-blue-500/50'
                        : key === 'evil'
                        ? 'bg-red-500/30 text-red-400 border border-red-500/50'
                        : 'bg-amber-500/30 text-amber-400 border border-amber-500/50'
                      : 'bg-avalon-card text-avalon-text hover:text-white hover:bg-avalon-purple/50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {mergedHistoryRecords.length === 0 ? (
            <div className="text-center py-8 text-avalon-text avalon-card">
              <Medal className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>暂无历史记录</p>
            </div>
          ) : (
            <div className="space-y-2">
              {mergedHistoryRecords.map((record, idx) => renderHistoryRecord(record, idx))}
              
              {historyHasMore && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={handleLoadMoreHistory}
                    disabled={isLoadingMore}
                    className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-avalon-primary to-avalon-primary/90 hover:from-avalon-primary/90 hover:to-avalon-primary/80 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2 border border-avalon-primary/20"
                  >
                    {isLoadingMore ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        <span>加载中...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5" />
                        <span>加载更多 ({historyGames.length} / {historyTotal})</span>
                      </>
                    )}
                  </button>
                </div>
              )}
              
              {!historyHasMore && historyGames.length > 0 && (
                <div className="text-center py-4 bg-avalon-surface/50 rounded-xl border border-avalon-border/50">
                  <p className="text-avalon-text text-sm font-medium flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4 text-avalon-primary/70" />
                    已显示全部 {historyTotal} 条记录
                  </p>
                </div>
              )}
            </div>
          )}
            </div>
          )}
        </>
      )}

      {/* V132: 角色统计面板已移除，改为详情页入口 */}

      {showAddModal && (
        <GameRecordModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
          }}
          editingGameId={editingGameId}
          playerCount={playerCount}
          setPlayerCount={setPlayerCount}
          winningFaction={winningFaction}
          setWinningFaction={setWinningFaction}
          victoryType={victoryType}
          setVictoryType={setVictoryType}
          mvpPlayer={mvpPlayer}
          setMvpPlayer={setMvpPlayer}
          taskResults={taskResults}
          setTaskResults={setTaskResults}
          playerInfos={playerInfos}
          gameDate={gameDate}
          setGameDate={setGameDate}
          gameNumber={gameNumber}
          setGameNumber={setGameNumber}
          gameDojoId={gameDojoId}
          setGameDojoId={setGameDojoId}
          gameSeasonId={gameSeasonId}
          setGameSeasonId={setGameSeasonId}
          calculatedScores={calculatedScores}
          dojos={dojos}
          seasons={seasons}
          onSubmit={handleSubmit}
          onDelete={() => setShowDeleteGameConfirm(true)}
          onSaveDraft={saveDraft}
          handlePlayerInfoChange={handlePlayerInfoChange}
          isSubmitting={isSubmitting}
          isDeleting={isDeleting}
        />
      )}

      <RulesModal isOpen={showRulesModal} onClose={() => setShowRulesModal(false)} />

      <DojoSeasonModal
        isOpen={showDojoSeasonModal}
        onClose={() => setShowDojoSeasonModal(false)}
        dojos={dojos}
        seasons={seasons}
        onDojosChange={setDojos}
        onSeasonsChange={setSeasons}
      />

      <EditConfirmModal
        isOpen={showEditConfirm}
        onClose={() => {
          setShowEditConfirm(false);
          setPendingEditGame(null);
        }}
        onConfirm={confirmEditGame}
      />

      <DeleteConfirmModal
        isOpen={showDeleteGameConfirm}
        onClose={() => setShowDeleteGameConfirm(false)}
        onConfirm={handleDeleteGame}
      />

      <ResetDataModal
        isOpen={showDataResetModal}
        onClose={() => setShowDataResetModal(false)}
        resetState={resetState}
        setResetState={setResetState}
        dojos={dojos}
        seasons={seasons}
        previewCount={resetPreviewCount}
        onDojoSelect={handleDojoSelection}
        onSeasonSelect={handleSeasonSelection}
        onSubmit={handleResetPasswordSubmit}
      />

      <DebugModal
        isOpen={showDebugModal}
        onClose={() => {
          setShowDebugModal(false);
          setDebugTargetPlayer(null);
          setDebugPlayerDetails([]);
        }}
        player={debugTargetPlayer}
        records={debugPlayerDetails}
        onRefresh={() => {
          if (debugTargetPlayer) {
            fetchPlayerDebugDetails(debugTargetPlayer.player_number);
          }
        }}
      />

      <DataAuditModal
        isOpen={showDataAuditModal}
        onClose={() => setShowDataAuditModal(false)}
      />

      <ExportImportModal
        isOpen={showExportImportModal}
        onClose={() => setShowExportImportModal(false)}
        games={historyGames}
        dojos={dojos}
        seasons={seasons}
      />

      {/* V270: 评论功能弹窗 */}
      {commentGameId !== null && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-avalon-card rounded-xl w-full max-w-lg border border-avalon-border shadow-2xl max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-avalon-border flex items-center justify-between sticky top-0 bg-avalon-card rounded-t-xl">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-avalon-gold" />
                对局评论
              </h3>
              <button
                onClick={handleCloseCommentModal}
                className="text-avalon-text hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {gameComments.length === 0 ? (
                <div className="text-center py-8 text-avalon-text">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">暂无评论，快来抢沙发吧！</p>
                </div>
              ) : (
                gameComments.map((comment) => (
                  <div key={comment.id} className="bg-avalon-bg/50 rounded-lg p-3 border border-avalon-card">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-avalon-gold">{comment.player_nickname}</span>
                      <span className="text-xs text-avalon-text">
                        {new Date(comment.created_at).toLocaleString('zh-CN', {
                          month: 'numeric',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-white whitespace-pre-wrap">{comment.comment_text}</p>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleEditComment(comment)}
                        className="text-xs text-avalon-purple hover:text-avalon-purple/80 transition-colors flex items-center gap-1"
                      >
                        <Edit2 className="w-3 h-3" />
                        编辑
                      </button>
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-xs text-avalon-red hover:text-avalon-red/80 transition-colors flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        删除
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t border-avalon-border sticky bottom-0 bg-avalon-card rounded-b-xl">
              <div className="space-y-2">
                <input
                  type="text"
                  value={commentNickname}
                  onChange={(e) => setCommentNickname(e.target.value)}
                  placeholder="请输入昵称（可选）"
                  className="w-full px-3 py-2 bg-avalon-input text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-avalon-purple/50"
                  disabled={isSubmittingComment}
                />
                <div className="flex gap-2">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="写下你的评论..."
                    className="flex-1 px-3 py-2 bg-avalon-input text-white rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-avalon-purple/50"
                    rows={2}
                    disabled={isSubmittingComment}
                  />
                  <div className="flex flex-col gap-2">
                    {editingCommentId && (
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 bg-avalon-bg text-white rounded-lg text-sm font-medium hover:bg-avalon-bg/80 transition-colors"
                      >
                        取消
                      </button>
                    )}
                    <button
                      onClick={handleSubmitComment}
                      disabled={!commentText.trim() || isSubmittingComment}
                      className="px-4 py-2 bg-avalon-purple text-white rounded-lg text-sm font-medium hover:bg-avalon-purple/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSubmittingComment ? '发送中...' : editingCommentId ? '更新' : '发送'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 扣分记录编辑弹窗 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-avalon-card rounded-xl p-6 w-full max-w-sm border border-avalon-border shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-4">确认删除</h3>
            <p className="text-avalon-text mb-6">确定要删除这条扣分记录吗？</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2 px-4 rounded-lg bg-avalon-card border border-avalon-border text-avalon-text hover:text-white"
              >
                取消
              </button>
              <button
                onClick={confirmDeletePenaltyRecord}
                className="flex-1 py-2 px-4 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}
      {editingPenaltyRecord && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-avalon-card rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto border border-avalon-purple/30">
            <div className="p-4 border-b border-avalon-purple/30 flex items-center justify-between sticky top-0 bg-avalon-card">
              <h3 className="text-lg font-bold text-white">编辑扣分记录</h3>
              <button onClick={() => setEditingPenaltyRecord(null)} className="text-avalon-text hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-avalon-text mb-1">玩家昵称</label>
                <input
                  type="text"
                  value={editingPenaltyRecord.player_nickname}
                  onChange={(e) => setEditingPenaltyRecord({ ...editingPenaltyRecord, player_nickname: e.target.value })}
                  className="w-full px-3 py-2 bg-avalon-input text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-avalon-text mb-1">扣分类型</label>
                <select
                  value={editingPenaltyRecord.penalty_type}
                  onChange={(e) => {
                    const newType = e.target.value as PenaltyType;
                    const scoreMap: Record<PenaltyType, number> = {
                      self_destruct: -2 * (editingPenaltyRecord.player_count || 6),
                      wrong_kill: -(editingPenaltyRecord.player_count || 6),
                      bug: -2 * (editingPenaltyRecord.player_count || 6),
                    };
                    setEditingPenaltyRecord({
                      ...editingPenaltyRecord,
                      penalty_type: newType,
                      penalty_score: scoreMap[newType],
                    });
                  }}
                  className="w-full px-3 py-2 bg-avalon-input text-white"
                >
                  {Object.entries(PENALTY_NAMES).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-avalon-text mb-1">游戏人数</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={editingPenaltyRecord.player_count || 6}
                  onChange={(e) => {
                    const newCount = Number(e.target.value);
                    const scoreMap: Record<string, number> = {
                      self_destruct: -2 * newCount,
                      wrong_kill: -newCount,
                      bug: -2 * newCount,
                    };
                    setEditingPenaltyRecord({
                      ...editingPenaltyRecord,
                      player_count: newCount,
                      penalty_score: scoreMap[editingPenaltyRecord.penalty_type] || -newCount,
                    });
                  }}
                  className="w-full px-3 py-2 bg-avalon-input text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-avalon-text mb-1">扣分数值</label>
                <input
                  type="number"
                  value={editingPenaltyRecord.penalty_score}
                  onChange={(e) => setEditingPenaltyRecord({ ...editingPenaltyRecord, penalty_score: Number(e.target.value) })}
                  className="w-full px-3 py-2 bg-avalon-input border border-avalon-border rounded text-amber-400 font-bold"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-avalon-text mb-1">日期</label>
                <input
                  type="date"
                  value={editingPenaltyRecord.penalty_date?.split('T')[0] || ''}
                  onChange={(e) => setEditingPenaltyRecord({ ...editingPenaltyRecord, penalty_date: e.target.value })}
                  className="w-full px-3 py-2 bg-avalon-input text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-avalon-text mb-1">道场</label>
                  <select
                    value={editingPenaltyRecord.dojo_id || ''}
                    onChange={(e) => setEditingPenaltyRecord({ ...editingPenaltyRecord, dojo_id: e.target.value ? Number(e.target.value) : null })}
                    className="w-full px-3 py-2 bg-avalon-input text-white"
                  >
                    <option value="">未选择</option>
                    {dojos.map(dojo => (
                      <option key={dojo.id} value={dojo.id}>{dojo.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-avalon-text mb-1">赛季</label>
<label className="block text-sm font-medium text-avalon-text mb-1">赛季</label>
                  <select
                    value={editingPenaltyRecord.season_id || ''}
                    onChange={(e) => setEditingPenaltyRecord({ ...editingPenaltyRecord, season_id: e.target.value ? Number(e.target.value) : null })}
                    className="w-full px-3 py-2 bg-avalon-input text-white"
                  >
                    <option value="">未选择</option>
                    {seasons.map(season => (
                      <option key={season.id} value={season.id}>{season.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-avalon-text mb-1">备注</label>
                <textarea
                  value={editingPenaltyRecord.reason || ''}
                  onChange={(e) => setEditingPenaltyRecord({ ...editingPenaltyRecord, reason: e.target.value })}
                  className="w-full px-3 py-2 bg-avalon-input text-white"
                  rows={2}
                />
              </div>
            </div>
            <div className="p-4 border-t border-avalon-purple/30 flex gap-3 sticky bottom-0 bg-avalon-card">
              <button
                onClick={() => setEditingPenaltyRecord(null)}
                className="flex-1 py-2 px-4 rounded-lg bg-avalon-card border border-avalon-border text-avalon-text hover:text-white"
              >
                取消
              </button>
              <button
                onClick={handleSaveEditedPenalty}
                className="flex-1 py-2 px-4 rounded-lg bg-avalon-purple text-white hover:bg-avalon-purple/80"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </ThemedBackground>
);
}
